import React, { useState, useEffect, useRef, useCallback } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import rizeeLogo from "../../../images/logo-white.svg";

// Fix Leaflet marker icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// GraphQL Endpoint & Strict Institute Scope
const SALES_API =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://rizee-college-backend-755214126997.us-south1.run.app/graphql"
    : "http://localhost:3008/graphql");
const INSTITUTE_ID = 51;

async function salesGQL(query, variables = {}) {
  const res = await fetch(SALES_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors?.length) throw new Error(json.errors[0].message);
  return json.data;
}

// Date Helper Functions
function getFormattedDate(d) {
  return d.toISOString().split("T")[0];
}

function getYesterdayDate() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return getFormattedDate(d);
}

function getTodayDate() {
  return getFormattedDate(new Date());
}

function getDateDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return getFormattedDate(d);
}

// ── SVG Vector Icon Components ──────────────────────────────────────────────────────

function RefreshIcon({ size = 14, color = "#FFF", spinning = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: spinning ? "rotate(360deg)" : "none",
        transition: "transform 0.5s ease",
      }}
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function PhoneIcon({ size = 13, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function WhatsAppIcon({ size = 13, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

function MapPinIcon({ size = 13, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

// ── Harmonized Status Color Mapping ──────────────────────────────────────────

const STATUS_COLOR_MAP = {
  new: {
    bg: "#EFF6FF",
    text: "#1D4ED8",
    border: "#BFDBFE",
    dot: "#3B82F6",
    label: "New Lead",
  },
  converted: {
    bg: "#ECFDF5",
    text: "#047857",
    border: "#A7F3D0",
    dot: "#10B981",
    label: "Engaged / Confirmed / Converted",
  },
  lost: {
    bg: "#FEF2F2",
    text: "#B91C1C",
    border: "#FECACA",
    dot: "#EF4444",
    label: "Not Interested / Lost",
  },
  default: {
    bg: "#F8FAFC",
    text: "#475569",
    border: "#E2E8F0",
    dot: "#64748B",
    label: "General Status",
  },
};

function getStatusStyle(statusStr, labelStr) {
  const s = String(statusStr || "").toLowerCase().trim();
  const l = String(labelStr || "").toLowerCase().trim();

  // 1. Engaged / Confirmed / Converted / Won -> Green
  if (
    l.includes("confirm") ||
    l.includes("convert") ||
    l.includes("engage") ||
    l.includes("won") ||
    s === "converted" ||
    s === "confirmed"
  ) {
    return STATUS_COLOR_MAP.converted;
  }

  // 2. New / Open -> Blue
  if (s === "new" || l.includes("new") || l.includes("open")) {
    return STATUS_COLOR_MAP.new;
  }

  // 3. Not Interested / Lost / Rejected -> Red
  if (
    l.includes("not interest") ||
    l.includes("lost") ||
    l.includes("reject") ||
    l.includes("drop") ||
    l.includes("cancel")
  ) {
    return STATUS_COLOR_MAP.lost;
  }

  return STATUS_COLOR_MAP.default;
}

function parseTs(ts) {
  if (!ts) return null;
  const s = String(ts).trim();
  if (/^\d{10}$/.test(s)) return new Date(parseInt(s) * 1000);
  if (/^\d{13}$/.test(s)) return new Date(parseInt(s));
  return new Date(s);
}

function fmtDateTime(ts) {
  const d = parseTs(ts);
  if (!d || isNaN(d)) return "—";
  return (
    d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) +
    " · " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

function StatusBadge({ status, label }) {
  const style = getStatusStyle(status, label);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: style.bg,
        color: style.text,
        border: `1px solid ${style.border}`,
        borderRadius: 20,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: style.dot,
          boxShadow: `0 0 6px ${style.dot}`,
        }}
      />
      {label || status}
    </span>
  );
}

function EmployeeAvatar({ name }) {
  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const bgList = [
    "#6366F1",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#3B82F6",
    "#06B6D4",
  ];
  const bg = bgList[Math.abs(hash) % bgList.length];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: bg,
          color: "#FFF",
          fontWeight: 800,
          fontSize: 11,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          flexShrink: 0,
        }}
      >
        {initials}
      </div>
      <span
        style={{
          fontWeight: 600,
          color: "#0F172A",
          fontSize: 13,
          whiteSpace: "nowrap",
        }}
      >
        {name || "Unassigned"}
      </span>
    </div>
  );
}

// Crisp Vector Map Location Pin Generator
function getStatusMarkerIcon(statusStr, labelStr) {
  const style = getStatusStyle(statusStr, labelStr);
  const color = style.dot;

  const svg = `<svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.16344 0 0 7.16344 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7.16344 24.8366 0 16 0Z" fill="${color}"/>
    <circle cx="16" cy="15" r="7" fill="#FFFFFF"/>
    <circle cx="16" cy="15" r="4" fill="${color}"/>
  </svg>`;

  return L.divIcon({
    html: `<div style="filter:drop-shadow(0px 4px 10px rgba(0,0,0,0.3));cursor:pointer">${svg}</div>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
    className: "",
  });
}

// ── Main Dashboard Component ───────────────────────────────────────────────────

export default function SalesAdminDashboard() {
  const [employees, setEmployees] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Date Range Presets: Defaults to 'yesterday', falls back to 'all' if yesterday has 0 leads
  const [datePreset, setDatePreset] = useState("yesterday");
  const [startDate, setStartDate] = useState(getYesterdayDate());
  const [endDate, setEndDate] = useState(getYesterdayDate());

  // Pagination state
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);

  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Detail drawer
  const [detailLead, setDetailLead] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailHistory, setDetailHistory] = useState([]);

  // GPS Map State & Mode ('pins' vs 'heatmap')
  const [mapViewMode, setMapViewMode] = useState("pins");
  const [gpsTracks, setGpsTracks] = useState([]);
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef({});

  // ── Initial load ────────────────────────────────────────────────────────────

  useEffect(() => {
    Promise.all([
      salesGQL(
        `query($i:Int!){ getSalesEmployees(institute_id:$i){ id name } }`,
        { i: INSTITUTE_ID },
      )
        .then((d) => setEmployees(d.getSalesEmployees || []))
        .catch(() => {}),
      salesGQL(
        `query($i:Int!){ getCRMLeadStatusTypes(institute_id:$i){ id label order_no } }`,
        { i: INSTITUTE_ID },
      )
        .then((d) => setStatusTypes(d.getCRMLeadStatusTypes || []))
        .catch(() => {}),
    ]);
    loadDashboard();
    loadLeads();
  }, []);

  // Handle Preset Date selection
  function handlePresetChange(presetKey) {
    setDatePreset(presetKey);
    if (presetKey === "yesterday") {
      const y = getYesterdayDate();
      setStartDate(y);
      setEndDate(y);
    } else if (presetKey === "today") {
      const t = getTodayDate();
      setStartDate(t);
      setEndDate(t);
    } else if (presetKey === "last7") {
      setStartDate(getDateDaysAgo(6));
      setEndDate(getTodayDate());
    } else if (presetKey === "last30") {
      setStartDate(getDateDaysAgo(29));
      setEndDate(getTodayDate());
    } else if (presetKey === "all") {
      setStartDate("");
      setEndDate("");
    }
  }

  // Load leads and GPS route when filters change
  useEffect(() => {
    setPage(1);
    loadLeads(1, sizePerPage);
    loadGPS();
  }, [selectedEmp, selectedStatus, startDate, endDate]);

  async function loadDashboard() {
    try {
      const d = await salesGQL(
        `query($i:Int!){ getAdminCRMDashboard(institute_id:$i){ total status_counts{ status status_label count } } }`,
        { i: INSTITUTE_ID },
      );
      setDashboard(d.getAdminCRMDashboard);
    } catch (e) {
      setError(e.message);
    }
  }

  // Server-Side Paginated Query for getAdminCRMLeads
  async function loadLeads(currentPage = page, currentSize = sizePerPage) {
    setLoading(true);
    setError(null);
    try {
      const d = await salesGQL(
        `query($i:Int!, $emp:String, $sf:String, $limit:Int, $offset:Int) {
          getAdminCRMLeads(institute_id:$i, employee_id:$emp, status_filter:$sf, limit:$limit, offset:$offset) {
            id institution_name contact_mobile description status status_label
            assigned_to latest_comment last_activity created_at followup_time
            latitude longitude address
          }
        }`,
        {
          i: INSTITUTE_ID,
          emp: selectedEmp !== "all" ? selectedEmp : null,
          sf: selectedStatus !== "all" ? selectedStatus : null,
          limit: currentSize,
          offset: (currentPage - 1) * currentSize,
        },
      );
      const fetchedLeads = d.getAdminCRMLeads || [];
      setLeads(fetchedLeads);

      // Evaluate if Yesterday contains any leads
      const y = getYesterdayDate();
      const startMs = new Date(y + "T00:00:00").getTime();
      const endMs = new Date(y + "T23:59:59").getTime();

      const hasYesterdayLeads = fetchedLeads.some((lead) => {
        const leadDate = parseTs(lead.created_at || lead.last_activity);
        if (!leadDate || isNaN(leadDate)) return false;
        const t = leadDate.getTime();
        return t >= startMs && t <= endMs;
      });

      if (!hasYesterdayLeads && datePreset === "yesterday") {
        setDatePreset("all");
        setStartDate("");
        setEndDate("");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function handleTableChange(type, { page: newPage, sizePerPage: newSize }) {
    if (type === "pagination") {
      setPage(newPage);
      setSizePerPage(newSize);
      loadLeads(newPage, newSize);
    }
  }

  async function loadGPS() {
    const empToFetch =
      selectedEmp !== "all"
        ? selectedEmp
        : employees.length > 0
        ? String(employees[0].id)
        : null;

    if (!empToFetch || !startDate) return setGpsTracks([]);

    try {
      const d = await salesGQL(
        `query($emp:String!, $date:String!){ getSalesGPSTracks(employee_id:$emp, track_date:$date){ latitude longitude timestamp } }`,
        { emp: empToFetch, date: startDate },
      );
      setGpsTracks(d.getSalesGPSTracks || []);
    } catch {
      setGpsTracks([]);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await Promise.all([loadDashboard(), loadLeads(), loadGPS()]);
    setTimeout(() => setRefreshing(false), 500);
  }

  async function openDetail(lead) {
    setDetailLead(lead);
    setShowDetail(true);
    setDetailHistory([]);
    try {
      const d = await salesGQL(
        `query($id:Int!){ getCRMLeadDetails(lead_id:$id){
          id institution_name contact_mobile contact_name description
          status status_label assigned_to created_at latitude longitude address
          history{
            id status status_label contact_via comments
            followup_time latitude longitude files created_at created_by
          }
        }}`,
        { id: lead.id },
      );
      if (d.getCRMLeadDetails) {
        setDetailLead(d.getCRMLeadDetails);
        setDetailHistory(d.getCRMLeadDetails.history || []);
      }
    } catch {
      /* Keep basic data */
    }
  }

  // Real-Time High-Performance Dynamic Lead Filtering across Rep, Status & Date Range
  const filteredLeads = leads.filter((lead) => {
    // 1. Employee Filter
    if (selectedEmp !== "all" && String(lead.assigned_to) !== String(selectedEmp)) {
      return false;
    }

    // 2. Status Filter
    if (selectedStatus !== "all") {
      const stStyle = getStatusStyle(lead.status, lead.status_label);
      if (selectedStatus === "new") {
        if (lead.status !== "new" && !stStyle.label.toLowerCase().includes("new")) {
          return false;
        }
      } else {
        const matchesId = String(lead.status) === String(selectedStatus);
        const matchesLabel = lead.status_label
          ?.toLowerCase()
          .includes(selectedStatus.toLowerCase());
        if (!matchesId && !matchesLabel) return false;
      }
    }

    // 3. Date Range Filter
    if (startDate || endDate) {
      const leadDate = parseTs(lead.created_at || lead.last_activity);
      if (leadDate && !isNaN(leadDate)) {
        const time = leadDate.getTime();
        const startMs = startDate ? new Date(startDate + "T00:00:00").getTime() : 0;
        const endMs = endDate ? new Date(endDate + "T23:59:59").getTime() : Infinity;
        if (time < startMs || time > endMs) return false;
      }
    }

    return true;
  });

  // ── Dynamic Map Route & Visit Markers Rendering ───────────────────────────────

  useEffect(() => {
    if (!mapRef.current) return;

    if (!leafletMap.current) {
      const map = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
      leafletMap.current = map;
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        {
          attribution: "© OpenStreetMap © CARTO",
          maxZoom: 19,
        },
      ).addTo(map);
    }

    const map = leafletMap.current;
    markersRef.current = {};

    map.eachLayer((layer) => {
      if (
        layer instanceof L.Marker ||
        layer instanceof L.Polyline ||
        layer instanceof L.CircleMarker
      ) {
        map.removeLayer(layer);
      }
    });

    const allCoords = [];

    // GPS route polyline (Emerald Green)
    if (gpsTracks.length > 1) {
      const line = gpsTracks.map((p) => [
        parseFloat(p.latitude),
        parseFloat(p.longitude),
      ]);
      L.polyline(line, {
        color: "#10B981",
        weight: 5,
        opacity: 0.9,
      }).addTo(map);

      L.circleMarker(line[0], {
        radius: 8,
        fillColor: "#059669",
        color: "#FFF",
        weight: 3,
        fillOpacity: 1,
      })
        .bindPopup("<b style='color:#059669'>Start Position</b>")
        .addTo(map);

      L.circleMarker(line[line.length - 1], {
        radius: 8,
        fillColor: "#10B981",
        color: "#FFF",
        weight: 3,
        fillOpacity: 1,
      })
        .bindPopup("<b style='color:#10B981'>Recorded Position</b>")
        .addTo(map);

      line.forEach((c) => allCoords.push(c));
    }

    // Lead Visit Pins for All Filtered Leads
    const leadsWithLocation = filteredLeads.filter(
      (l) =>
        l.latitude &&
        l.longitude &&
        parseFloat(l.latitude) !== 0 &&
        parseFloat(l.longitude) !== 0,
    );

    leadsWithLocation.forEach((lead) => {
      const lat = parseFloat(lead.latitude),
        lng = parseFloat(lead.longitude);
      allCoords.push([lat, lng]);

      const style = getStatusStyle(lead.status, lead.status_label);

      if (mapViewMode === "heatmap") {
        // Heatmap Density Ring (Outer 400m aura)
        L.circle([lat, lng], {
          radius: 450,
          fillColor: style.dot,
          fillOpacity: 0.2,
          stroke: false,
        }).addTo(map);

        // Heatmap Core Ring (Inner 180m core)
        L.circle([lat, lng], {
          radius: 180,
          fillColor: style.dot,
          fillOpacity: 0.5,
          stroke: false,
        }).addTo(map);

        const marker = L.circleMarker([lat, lng], {
          radius: 6,
          fillColor: "#FFFFFF",
          color: style.dot,
          weight: 2,
          fillOpacity: 1,
        })
          .bindPopup(
            `<div style="font-family:sans-serif;padding:2px">
              <div style="font-size:11px;font-weight:800;color:#6366F1">Lead Generation Cluster</div>
              <div style="font-size:13px;font-weight:800;color:#0F172A;margin-top:2px">${lead.institution_name}</div>
              <div style="font-size:11px;color:#64748B">${fmtDateTime(lead.created_at)}</div>
              <div style="font-size:12px;color:${style.text};font-weight:700;margin-top:4px">Status: ${statusLabel(lead.status, lead.status_label)}</div>
            </div>`,
          )
          .addTo(map);

        markersRef.current[lead.id] = marker;
      } else {
        const pinIcon = getStatusMarkerIcon(lead.status, lead.status_label);

        const marker = L.marker([lat, lng], { icon: pinIcon })
          .bindPopup(
            `<div style="font-family:sans-serif;padding:2px">
              <div style="font-size:13px;font-weight:800;color:#0F172A">${lead.institution_name}</div>
              <div style="font-size:11px;color:#64748B;margin-top:2px">${fmtDateTime(lead.created_at)}</div>
              <div style="font-size:12px;color:${style.text};font-weight:700;margin-top:4px">Status: ${statusLabel(lead.status, lead.status_label)}</div>
              ${
                lead.contact_mobile
                  ? `<div style="margin-top:6px"><a href="tel:${lead.contact_mobile}" style="font-size:11px;color:#2563EB;font-weight:700;text-decoration:none">Call ${lead.contact_mobile}</a></div>`
                  : ""
              }
            </div>`,
          )
          .addTo(map);

        markersRef.current[lead.id] = marker;
      }
    });

    if (allCoords.length > 0) {
      map.fitBounds(allCoords, { padding: [30, 30], maxZoom: 16 });
    }
  }, [gpsTracks, filteredLeads, selectedEmp, mapViewMode]);

  function locateOnMap(lead) {
    if (!lead.latitude || parseFloat(lead.latitude) === 0) {
      return alert("No GPS coordinates recorded for this lead visit.");
    }
    const lat = parseFloat(lead.latitude);
    const lng = parseFloat(lead.longitude);
    if (leafletMap.current) {
      leafletMap.current.setView([lat, lng], 16);
      if (markersRef.current[lead.id]) {
        markersRef.current[lead.id].openPopup();
      }
    }
  }

  const statusLabel = useCallback(
    (status, labelFromRow) => {
      if (labelFromRow) return labelFromRow;
      if (status === "new") return "New";
      const st = statusTypes.find((s) => String(s.id) === String(status));
      return st?.label || status;
    },
    [statusTypes],
  );

  const empName = useCallback(
    (id) => {
      const e = employees.find((e) => String(e.id) === String(id));
      return e?.name || id;
    },
    [employees],
  );

  // Table Setup
  const { SearchBar } = Search;

  const columns = [
    {
      dataField: "institution_name",
      text: "LEAD / INSTITUTION",
      sort: true,
      formatter: (v, row) => (
        <div>
          <div style={{ fontWeight: 800, color: "#0F172A", fontSize: 13 }}>
            {v}
          </div>
          {row.address && (
            <div
              style={{
                fontSize: 11,
                color: "#64748B",
                marginTop: 2,
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <MapPinIcon size={11} color="#64748B" />
              <span>{row.address}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      dataField: "assigned_to",
      text: "REPRESENTATIVE",
      sort: true,
      formatter: (v) => <EmployeeAvatar name={empName(v)} />,
    },
    {
      dataField: "status",
      text: "STATUS",
      sort: true,
      formatter: (v, row) => (
        <StatusBadge status={v} label={statusLabel(v, row.status_label)} />
      ),
    },
    {
      dataField: "created_at",
      text: "CREATED TIME",
      sort: true,
      formatter: (v) => (
        <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>
          {fmtDateTime(v)}
        </span>
      ),
    },
    {
      dataField: "contact_mobile",
      text: "PHONE",
      sort: true,
      formatter: (v) =>
        v ? (
          <a
            href={`tel:${v}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: "#4F46E5",
              fontWeight: 700,
              textDecoration: "none",
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <PhoneIcon size={12} color="#4F46E5" />
            <span>{v}</span>
          </a>
        ) : (
          <span style={{ color: "#94A3B8" }}>—</span>
        ),
    },
    {
      dataField: "actions",
      text: "ACTIONS",
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right" },
      formatter: (_, row) => (
        <div
          style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
          onClick={(e) => e.stopPropagation()}
        >
          {row.contact_mobile && (
            <>
              <a
                href={`tel:${row.contact_mobile}`}
                title="Call Direct"
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  color: "#2563EB",
                  fontSize: 11,
                  fontWeight: 700,
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <PhoneIcon size={11} color="#2563EB" />
                <span>Call</span>
              </a>
              <a
                href={`https://wa.me/${row.contact_mobile}`}
                target="_blank"
                rel="noreferrer"
                title="WhatsApp Chat"
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: "#ECFDF5",
                  border: "1px solid #A7F3D0",
                  color: "#059669",
                  fontSize: 11,
                  fontWeight: 700,
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <WhatsAppIcon size={11} color="#059669" />
                <span>WhatsApp</span>
              </a>
            </>
          )}

          {row.latitude && parseFloat(row.latitude) !== 0 && (
            <button
              onClick={() => locateOnMap(row)}
              title="Locate pin on map"
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                background: "#EEF2FF",
                border: "1px solid #C7D2FE",
                color: "#4F46E5",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <MapPinIcon size={11} color="#4F46E5" />
              <span>Map</span>
            </button>
          )}
          <button
            onClick={() => openDetail(row)}
            style={{
              padding: "4px 10px",
              borderRadius: 6,
              background: "#0F172A",
              border: "none",
              color: "#FFF",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Details
          </button>
        </div>
      ),
    },
  ];

  // Dynamic Calculated Stats based on Active Filtered Leads
  const totalFilteredLeads = filteredLeads.length;
  const convertedCount = filteredLeads.filter((l) => {
    const st = getStatusStyle(l.status, l.status_label);
    return st.dot === "#10B981";
  }).length;
  const conversionRate =
    totalFilteredLeads > 0
      ? Math.round((convertedCount / totalFilteredLeads) * 100)
      : 0;

  return (
    <div
      style={{
        background: "#F8FAFC",
        minHeight: "100vh",
        fontFamily:
          "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#0F172A",
      }}
    >
      {/* ── Responsive CSS Rules ── */}
      <style>{`
        .sales-workspace-grid {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 20px;
          align-items: start;
        }
        .sales-table-col {
          grid-column: span 7;
        }
        .sales-map-col {
          grid-column: span 5;
        }

        @media (max-width: 1200px) {
          .sales-table-col {
            grid-column: span 12 !important;
          }
          .sales-map-col {
            grid-column: span 12 !important;
            position: relative !important;
            top: 0 !important;
          }
        }

        @media (max-width: 768px) {
          .sales-header-container {
            padding: 12px 16px !important;
          }
          .sales-metrics-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .sales-main-container {
            padding: 12px !important;
          }
          .sales-map-canvas {
            height: 380px !important;
          }
        }

        @media (max-width: 480px) {
          .sales-metrics-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* ── Top Header Bar with Rizee Logo ── */}
      <header
        className="sales-header-container"
        style={{
          background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
          color: "#FFFFFF",
          padding: "16px 28px",
          boxShadow: "0 4px 20px -2px rgba(15, 23, 42, 0.25)",
        }}
      >
        <div
          style={{
            maxWidth: 1600,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          {/* Rizee Logo Branding & Title */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                height: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "2px 8px",
                background: "rgba(255,255,255,0.08)",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <img
                src={rizeeLogo}
                alt="Rizee Logo"
                style={{ height: 26, width: "auto", objectFit: "contain" }}
              />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h1 style={{ margin: 0, fontSize: 18, fontWeight: 900, letterSpacing: "-0.5px" }}>
                  Sales CRM & Field Intelligence
                </h1>
                <span
                  style={{
                    background: "rgba(99, 102, 241, 0.25)",
                    border: "1px solid rgba(99, 102, 241, 0.4)",
                    color: "#A5B4FC",
                    fontSize: 11,
                    fontWeight: 800,
                    padding: "2px 8px",
                    borderRadius: 14,
                  }}
                >
                  INSTITUTE #51
                </span>
              </div>
            </div>
          </div>

          {/* Controls toolbar */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {/* Date Range Presets */}
            <div style={{ display: "flex", gap: 4, background: "rgba(255, 255, 255, 0.08)", padding: 3, borderRadius: 8 }}>
              {[
                { key: "all", label: "All Time" },
                { key: "yesterday", label: "Yesterday" },
                { key: "today", label: "Today" },
                { key: "last7", label: "Last 7 Days" },
                { key: "last30", label: "Last 30 Days" },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => handlePresetChange(p.key)}
                  style={{
                    background: datePreset === p.key ? "#4F46E5" : "transparent",
                    color: datePreset === p.key ? "#FFF" : "#94A3B8",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Custom Date Range Inputs */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.14)",
                borderRadius: 8,
                padding: "4px 10px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setDatePreset("custom");
                  setStartDate(e.target.value);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 11,
                  outline: "none",
                  cursor: "pointer",
                }}
              />
              <span style={{ color: "#94A3B8", fontSize: 11 }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setDatePreset("custom");
                  setEndDate(e.target.value);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 11,
                  outline: "none",
                  cursor: "pointer",
                }}
              />
            </div>

            {/* Employee Selection */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.14)",
                borderRadius: 8,
                padding: "4px 10px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <label style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", margin: 0 }}>
                EMPLOYEE:
              </label>
              <select
                value={selectedEmp}
                onChange={(e) => setSelectedEmp(e.target.value)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 12,
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="all" style={{ color: "#0F172A" }}>
                  All Sales Employees ({employees.length})
                </option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id} style={{ color: "#0F172A" }}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Selection */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.14)",
                borderRadius: 8,
                padding: "4px 10px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <label style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", margin: 0 }}>
                STATUS:
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 12,
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="all" style={{ color: "#0F172A" }}>All Statuses</option>
                <option value="new" style={{ color: "#0F172A" }}>New / Open</option>
                {statusTypes.map((s) => (
                  <option key={s.id} value={String(s.id)} style={{ color: "#0F172A" }}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleRefresh}
              style={{
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                color: "#FFF",
                border: "none",
                borderRadius: 8,
                padding: "7px 16px",
                fontWeight: 800,
                fontSize: 12,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              }}
            >
              <RefreshIcon size={14} color="#FFF" spinning={refreshing} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </header>

      {/* Connection Notice */}
      {error && (
        <div
          style={{
            background: "#FEF2F2",
            color: "#991B1B",
            padding: "8px 28px",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Notice: {error}
        </div>
      )}

      {/* ── Top Metric Cards Strip ── */}
      <section style={{ padding: "16px 28px 0" }}>
        <div
          className="sales-metrics-grid"
          style={{
            maxWidth: 1600,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: 16,
              border: "1px solid #E2E8F0",
              borderLeft: "4px solid #3B82F6",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: "#64748B" }}>
              TOTAL LEADS
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#0F172A", marginTop: 2 }}>
              {totalFilteredLeads}
            </div>
            <div style={{ fontSize: 11, color: "#2563EB", fontWeight: 700, marginTop: 2 }}>
              Matching current filter selection
            </div>
          </div>

          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: 16,
              border: "1px solid #E2E8F0",
              borderLeft: "4px solid #10B981",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: "#64748B" }}>
              CONVERSION RATE
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#059669", marginTop: 2 }}>
              {conversionRate}%
            </div>
            <div style={{ fontSize: 11, color: "#059669", fontWeight: 700, marginTop: 2 }}>
              {convertedCount} converted / engaged
            </div>
          </div>

          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: 16,
              border: "1px solid #E2E8F0",
              borderLeft: "4px solid #F59E0B",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: "#64748B" }}>
              SALES EMPLOYEES
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#D97706", marginTop: 2 }}>
              {employees.length}
            </div>
            <div style={{ fontSize: 11, color: "#D97706", fontWeight: 700, marginTop: 2 }}>
              Assigned sales team
            </div>
          </div>

          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: 16,
              border: "1px solid #E2E8F0",
              borderLeft: "4px solid #0F172A",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: "#64748B" }}>
              STAGES
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#0F172A", marginTop: 2 }}>
              {statusTypes.length + 1}
            </div>
            <div style={{ fontSize: 11, color: "#475569", fontWeight: 700, marginTop: 2 }}>
              Active pipeline steps
            </div>
          </div>
        </div>
      </section>

      {/* ── Integrated Workspace ── */}
      <main className="sales-main-container" style={{ maxWidth: 1600, margin: "0 auto", padding: 24 }}>
        <div
          className="sales-workspace-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 20,
            alignItems: "start",
          }}
        >
          {/* Left Column (7 cols): Table Directory with Pagination */}
          <div
            className="sales-table-col"
            style={{
              gridColumn: "span 7",
              background: "#FFFFFF",
              borderRadius: 20,
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.04)",
              padding: 20,
            }}
          >
            <ToolkitProvider
              keyField="id"
              data={filteredLeads}
              columns={columns}
              search
            >
              {(props) => (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                      flexWrap: "wrap",
                      gap: 12,
                    }}
                  >
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: "#0F172A" }}>
                        Leads Directory ({filteredLeads.length})
                      </h3>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748B" }}>
                        Showing leads matching current filter selections.
                      </p>
                    </div>

                    <div style={{ maxWidth: 240, width: "100%" }}>
                      <SearchBar
                        {...props.searchProps}
                        placeholder="Search lead..."
                        style={{
                          fontSize: 12,
                          borderRadius: 8,
                          border: "1px solid #CBD5E1",
                          padding: "6px 12px",
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>

                  {/* Status Filter Pills */}
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginBottom: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      onClick={() => setSelectedStatus("all")}
                      style={{
                        background: selectedStatus === "all" ? "#0F172A" : "#F1F5F9",
                        color: selectedStatus === "all" ? "#FFF" : "#475569",
                        border: "none",
                        borderRadius: 14,
                        padding: "4px 12px",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      All ({filteredLeads.length})
                    </button>
                    <button
                      onClick={() => setSelectedStatus("new")}
                      style={{
                        background: selectedStatus === "new" ? "#3B82F6" : "#F1F5F9",
                        color: selectedStatus === "new" ? "#FFF" : "#475569",
                        border: "none",
                        borderRadius: 14,
                        padding: "4px 12px",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      New
                    </button>
                    {statusTypes.map((st) => {
                      const isSelected = selectedStatus === String(st.id);
                      const style = getStatusStyle(st.id, st.label);
                      return (
                        <button
                          key={st.id}
                          onClick={() => setSelectedStatus(String(st.id))}
                          style={{
                            background: isSelected ? style.dot : "#F1F5F9",
                            color: isSelected ? "#FFF" : "#475569",
                            border: "none",
                            borderRadius: 14,
                            padding: "4px 12px",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {st.label}
                        </button>
                      );
                    })}
                  </div>

                  {loading ? (
                    <div style={{ textAlign: "center", padding: 40, color: "#64748B", fontSize: 13 }}>
                      Loading leads...
                    </div>
                  ) : filteredLeads.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 40, color: "#64748B", fontSize: 13 }}>
                      No leads match current filter criteria.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <BootstrapTable
                        {...props.baseProps}
                        remote
                        onTableChange={handleTableChange}
                        bordered={false}
                        hover
                        classes="table-sm align-middle"
                        rowStyle={{ cursor: "pointer" }}
                        rowEvents={{
                          onClick: (_, row) => locateOnMap(row),
                        }}
                        pagination={paginationFactory({
                          page,
                          sizePerPage,
                          totalSize: dashboard?.total || (leads.length ? 100 : 0),
                          showTotal: true,
                          sizePerPageList: [10, 25, 50],
                        })}
                      />
                    </div>
                  )}
                </>
              )}
            </ToolkitProvider>
          </div>

          {/* Right Column (5 cols): Integrated Live GPS & Visit Map */}
          <div
            className="sales-map-col"
            style={{
              gridColumn: "span 5",
              background: "#FFFFFF",
              borderRadius: 20,
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.04)",
              padding: 20,
              position: "sticky",
              top: 20,
            }}
          >
            {/* Map Header Toolbar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: "#0F172A" }}>
                  Live Field Map
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748B" }}>
                  Showing {leadsWithLocationCount(filteredLeads)} location pins for active filters
                </p>
              </div>

              {/* Pins vs Heatmap View Toggle */}
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  background: "#F1F5F9",
                  padding: 3,
                  borderRadius: 8,
                  border: "1px solid #CBD5E1",
                }}
              >
                <button
                  onClick={() => setMapViewMode("pins")}
                  style={{
                    background: mapViewMode === "pins" ? "#0F172A" : "transparent",
                    color: mapViewMode === "pins" ? "#FFF" : "#64748B",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  📍 Pins View
                </button>
                <button
                  onClick={() => setMapViewMode("heatmap")}
                  style={{
                    background: mapViewMode === "heatmap" ? "#EF4444" : "transparent",
                    color: mapViewMode === "heatmap" ? "#FFF" : "#64748B",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  🔥 Heatmap Density
                </button>
              </div>
            </div>

            {/* Dynamic Map Legend Bar */}
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 12,
                padding: "8px 12px",
                background: "#F8FAFC",
                borderRadius: 10,
                border: "1px solid #E2E8F0",
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 800, color: "#64748B", alignSelf: "center", letterSpacing: 0.5 }}>
                STATUS LEGEND:
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#2563EB", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3B82F6", boxShadow: "0 0 6px #3B82F6" }} /> New
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#059669", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", boxShadow: "0 0 6px #10B981" }} /> Engaged / Confirmed / Converted
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#B91C1C", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", boxShadow: "0 0 6px #EF4444" }} /> Not Interested / Lost
              </span>
            </div>

            {/* Map Canvas */}
            <div
              className="sales-map-canvas"
              style={{
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                height: 480,
                width: "100%",
              }}
            >
              <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
            </div>
          </div>
        </div>
      </main>

      {/* Lead Detail Modal */}
      {showDetail && detailLead && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => setShowDetail(false)}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 20,
              width: "100%",
              maxWidth: 640,
              maxHeight: "85vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
                color: "#FFF",
                padding: "16px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>
                  {detailLead.institution_name}
                </h3>
                {detailLead.contact_mobile && (
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                    Mobile: {detailLead.contact_mobile}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowDetail(false)}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  color: "#FFF",
                  cursor: "pointer",
                  fontWeight: 800,
                  fontSize: 16,
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
              <div
                style={{
                  background: "#F8FAFC",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 20,
                  border: "1px solid #E2E8F0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 13, color: "#64748B" }}>
                    Assigned Representative:{" "}
                    <b style={{ color: "#0F172A" }}>
                      {empName(detailLead.assigned_to)}
                    </b>
                  </span>
                  <StatusBadge
                    status={detailLead.status}
                    label={statusLabel(
                      detailLead.status,
                      detailLead.status_label,
                    )}
                  />
                </div>

                {detailLead.contact_name && (
                  <div style={{ fontSize: 13, color: "#334155" }}>
                    Contact Person: {detailLead.contact_name}
                  </div>
                )}

                {detailLead.description && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#475569",
                      marginTop: 8,
                      lineHeight: 1.5,
                    }}
                  >
                    {detailLead.description}
                  </div>
                )}
              </div>

              <h4 style={{ fontSize: 15, fontWeight: 900, color: "#0F172A", marginBottom: 12 }}>
                Activity Timeline ({detailHistory.length})
              </h4>

              {detailHistory.length === 0 ? (
                <div style={{ fontSize: 13, color: "#94A3B8", textAlign: "center", padding: 20 }}>
                  No history recorded yet.
                </div>
              ) : (
                detailHistory.map((h, i) => (
                  <div
                    key={h.id || i}
                    style={{
                      paddingBottom: 12,
                      marginBottom: 12,
                      borderBottom: "1px solid #F1F5F9",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>
                      {fmtDateTime(h.created_at)}
                    </div>
                    {h.status_label && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#6366F1", marginTop: 2 }}>
                        {h.status_label}
                      </div>
                    )}
                    {h.comments && (
                      <div style={{ fontSize: 13, color: "#334155", marginTop: 4 }}>
                        {h.comments}
                      </div>
                    )}
                    {h.files && h.files.length > 0 && (
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        {h.files.map((url, fi) => (
                          <a key={fi} href={url} target="_blank" rel="noreferrer">
                            <img
                              src={url}
                              alt="proof"
                              style={{
                                width: 60,
                                height: 60,
                                borderRadius: 8,
                                objectFit: "cover",
                                border: "1px solid #E2E8F0",
                              }}
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function leadsWithLocationCount(lList) {
    return lList.filter(
      (l) =>
        l.latitude &&
        l.longitude &&
        parseFloat(l.latitude) !== 0 &&
        parseFloat(l.longitude) !== 0,
    ).length;
  }
}
